import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, LightbulbIcon } from 'lucide-react';
import { Question } from './types';
import { SelectionCard } from './SelectionCard';
import { RankingInstruction } from './RankingInstruction';
interface QuestionScreenProps {
  question: Question;
  selections: string[];
  otherText?: string;
  onSelect: (optionId: string) => void;
  onOtherTextChange?: (text: string) => void;
  onClearRanking?: () => void;
  onNext: () => void;
  onBack: () => void;
}
export function QuestionScreen({
  question,
  selections,
  otherText = '',
  onSelect,
  onOtherTextChange,
  onClearRanking,
  onNext,
  onBack
}: QuestionScreenProps) {
  const hasSelection = selections.length > 0;
  const isOtherSelected = selections.includes('other');
  // For ranking, we might want to require all options to be ranked, or just at least one.
  // Let's stick to "at least one" to enable the Next button, consistent with other types.
  const canProceed = hasSelection && (!isOtherSelected || otherText.trim().length > 0);
  const handleSelect = (optionId: string) => {
    onSelect(optionId);
  };
  const isOptionDisabled = (optionId: string) => {
    if (question.type === 'max-2') {
      return selections.length >= 2 && !selections.includes(optionId);
    }
    return false;
  };
  const getRankNumber = (optionId: string) => {
    if (question.type !== 'ranking') return undefined;
    const index = selections.indexOf(optionId);
    return index !== -1 ? index + 1 : undefined;
  };
  return <motion.div key={question.id} initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} transition={{
    duration: 0.4,
    ease: 'easeInOut'
  }} className="flex flex-col w-full" data-id="element-29">
      <div className="mb-8" data-id="element-30">
        <h2 className="text-3xl font-bold text-navy mb-3 tracking-tight" data-id="element-31">
          {question.headline}
        </h2>
        {question.subtitle && <p className="text-lg text-slate-500 font-medium" data-id="element-32">
            {question.subtitle}
          </p>}
      </div>

      {question.type === 'ranking' && <RankingInstruction onClear={onClearRanking || (() => {})} hasSelections={hasSelection} data-id="element-33" />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-34">
        {question.options.map(option => <SelectionCard key={option.id} label={option.label} icon={option.icon} selected={selections.includes(option.id)} disabled={isOptionDisabled(option.id)} onClick={() => handleSelect(option.id)} rankNumber={getRankNumber(option.id)} data-id="element-35" />)}
      </div>

      <AnimatePresence data-id="element-36">
        {question.hasOther && isOtherSelected && <motion.div initial={{
        opacity: 0,
        height: 0,
        marginTop: 0
      }} animate={{
        opacity: 1,
        height: 'auto',
        marginTop: 16
      }} exit={{
        opacity: 0,
        height: 0,
        marginTop: 0
      }} className="mb-6 overflow-hidden" data-id="element-37">
            <label htmlFor="other-input" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-38">
              Please specify
            </label>
            <input id="other-input" type="text" value={otherText} onChange={e => onOtherTextChange && onOtherTextChange(e.target.value)} placeholder="Type your answer here..." className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-minerva focus:ring-0 transition-colors outline-none text-navy" autoFocus data-id="element-39" />
          </motion.div>}
      </AnimatePresence>

      <div className="min-h-[80px] mb-8" data-id="element-40">
        <AnimatePresence mode="wait" data-id="element-41">
          {hasSelection && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="p-4 bg-minerva/10 rounded-xl border border-minerva/20 flex items-start gap-3" data-id="element-42">
              <LightbulbIcon className="w-5 h-5 text-minerva shrink-0 mt-0.5" data-id="element-43" />
              <p className="text-sm text-navy font-medium leading-relaxed" data-id="element-44">
                {question.reward}
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between pt-6 border-t border-slate-200 mt-auto" data-id="element-45">
        <button onClick={onBack} className="flex items-center gap-2 px-5 py-3 text-slate-500 font-medium rounded-lg hover:bg-slate-100 transition-colors" data-id="element-46">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-47" />
          <span data-id="element-48">Back</span>
        </button>

        <button onClick={onNext} disabled={!canProceed} className={`flex items-center gap-2 px-8 py-3 font-semibold rounded-xl transition-all ${canProceed ? 'bg-minerva text-white hover:bg-minerva-600 hover:shadow-lg hover:shadow-minerva/20 active:scale-[0.98]' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`} data-id="element-49">
          <span data-id="element-50">{question.buttonText || 'Next'}</span>
          {question.buttonText === 'Finish' ? <CheckCircle2Icon className="w-5 h-5" data-id="element-51" /> : <ArrowRightIcon className="w-5 h-5" data-id="element-52" />}
        </button>
      </div>
    </motion.div>;
}